import React, { useEffect, useMemo, useRef, useState } from "react";
import { css } from "@emotion/css";
import type {
  AskLane,
  CoachMessageUiInteractionSpec,
  LearnerMessageUiInteractionSpec,
} from "../../types/state";
import { MathMarkdown } from "../../components/MathMarkdown";
import { PendingIndicator } from "../../components/PendingIndicator";

/**
 * The Ask thread — a FE-local modal over the durable ``scope.ask`` lane.
 *
 * It is intentionally NOT a server overlay (``state.overlay`` is replaced
 * by the wrap and other server flows): opening / closing Ask is pure
 * client UX and must never disturb the activity step.  The thread itself
 * is server-backed, so closing and reopening shows the same conversation.
 *
 * Three quick options sit at the top:
 *   • Feels too easy / too hard → regenerate the ACTIVITY step harder /
 *     easier (closes the modal so the learner sees the new step).
 *   • How is this relevant?     → an ask-lane chat turn (stays open).
 * …plus a free-text box for anything else.
 */

const backdropStyle = css`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: var(--color-backdrop, rgba(0, 0, 0, 0.45));
  backdrop-filter: blur(2px);

  @media (min-width: 640px) {
    align-items: center;
  }
`;

const panelStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  max-height: 86vh;
  background: var(--color-bg);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 18px 18px 0 0;
  box-shadow: 0 -12px 40px -16px var(--color-shadow-strong, rgba(0, 0, 0, 0.5));
  overflow: hidden;

  @media (min-width: 640px) {
    border-radius: 18px;
  }
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-border-subtle);
`;

const titleStyle = css`
  font-weight: 700;
  font-size: 1.02em;
`;

const closeBtnStyle = css`
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1.4em;
  line-height: 1;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 8px;

  &:hover { background: var(--color-bg-subtle); color: var(--color-text); }
`;

const quickRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border-subtle);
`;

const quickBtnStyle = css`
  flex: 1 1 auto;
  white-space: nowrap;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    background: var(--color-primary-muted);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const threadStyle = css`
  flex: 1;
  min-height: 120px;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const emptyStyle = css`
  color: var(--color-text-muted);
  font-size: 0.9em;
  text-align: center;
  margin: auto 0;
  padding: 12px;
  line-height: 1.5;
`;

const bubbleBaseStyle = css`
  max-width: 86%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 0.94em;
  line-height: 1.55;
  white-space: pre-wrap;

  p { margin: 0.35em 0; }
  p:first-of-type { margin-top: 0; }
  p:last-of-type { margin-bottom: 0; }
  code {
    background: var(--color-bg-raised);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.92em;
  }
`;

const coachBubbleStyle = css`
  align-self: flex-start;
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border-subtle);
  border-bottom-left-radius: 4px;
`;

const learnerBubbleStyle = css`
  align-self: flex-end;
  background: var(--color-primary-muted);
  color: var(--color-text);
  border-bottom-right-radius: 4px;
`;

const composerStyle = css`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--color-border-subtle);
`;

const textareaStyle = css`
  flex: 1;
  min-height: 42px;
  max-height: 140px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 10px 12px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: inherit;
  font-size: 0.94em;
  line-height: 1.4;
  box-sizing: border-box;
  resize: none;

  &:focus { outline: none; border-color: var(--color-primary); }
  &::placeholder { color: var(--color-text-muted); }
`;

const sendBtnStyle = css`
  flex-shrink: 0;
  padding: 10px 18px;
  border: none;
  border-radius: 12px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.94em;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) { background: var(--color-primary-hover); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

interface AskModalProps {
  ask: AskLane;
  /** True while a coach reply is streaming — disables sending. */
  streaming: boolean;
  onClose: () => void;
  onSendText: (text: string) => void;
  onAskRelevance: () => void;
  onRegenerate: (button: "too_easy" | "too_hard") => void;
}

export const AskModal: React.FC<AskModalProps> = ({
  ask,
  streaming,
  onClose,
  onSendText,
  onAskRelevance,
  onRegenerate,
}) => {
  const [draft, setDraft] = useState("");
  const threadRef = useRef<HTMLDivElement>(null);

  const messages = useMemo(
    () =>
      (ask.interactionOrder ?? [])
        .map((id) => ask.interactions[id])
        .filter(Boolean),
    [ask.interactionOrder, ask.interactions],
  );

  // The streamed message body of the last coach turn is the live signal —
  // keep the thread pinned to the bottom as it grows.
  const lastCoachLen =
    (messages[messages.length - 1] as CoachMessageUiInteractionSpec | undefined)?.message
      ?.length ?? 0;
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, lastCoachLen]);

  // Escape closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const send = () => {
    const text = draft.trim();
    if (!text || streaming) return;
    onSendText(text);
    setDraft("");
  };

  return (
    <div
      className={backdropStyle}
      onClick={onClose}
      data-testid="ask-modal-backdrop"
    >
      <div
        className={panelStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Ask the coach"
        data-testid="ask-modal"
      >
        <div className={headerStyle}>
          <span className={titleStyle}>Ask the coach</span>
          <button
            type="button"
            className={closeBtnStyle}
            onClick={onClose}
            aria-label="Close"
            data-testid="ask-modal-close"
          >
            ×
          </button>
        </div>

        <div className={quickRowStyle}>
          <button
            type="button"
            className={quickBtnStyle}
            onClick={() => onRegenerate("too_easy")}
            disabled={streaming}
            data-testid="ask-quick-too-easy"
          >
            Feels too easy
          </button>
          <button
            type="button"
            className={quickBtnStyle}
            onClick={() => onRegenerate("too_hard")}
            disabled={streaming}
            data-testid="ask-quick-too-hard"
          >
            Feels too hard
          </button>
          <button
            type="button"
            className={quickBtnStyle}
            onClick={onAskRelevance}
            disabled={streaming}
            data-testid="ask-quick-relevance"
          >
            How is this relevant?
          </button>
        </div>

        <div className={threadStyle} ref={threadRef} data-testid="ask-thread">
          {messages.length === 0 ? (
            <div className={emptyStyle}>
              Stuck, curious, or want this tuned to you? Ask anything — or use a
              quick option above.
            </div>
          ) : (
            messages.map((m) => {
              if (m.kind === "learner_message") {
                const lm = m as LearnerMessageUiInteractionSpec;
                return (
                  <div
                    key={lm.interactionId}
                    className={`${bubbleBaseStyle} ${learnerBubbleStyle}`}
                    data-testid="ask-msg-learner"
                  >
                    {lm.text}
                  </div>
                );
              }
              if (m.kind === "coach_message") {
                const cm = m as CoachMessageUiInteractionSpec;
                const body = cm.message ?? "";
                return (
                  <div
                    key={cm.interactionId}
                    className={`${bubbleBaseStyle} ${coachBubbleStyle}`}
                    data-testid="ask-msg-coach"
                  >
                    {body ? (
                      <MathMarkdown>{body}</MathMarkdown>
                    ) : (
                      <PendingIndicator variant="inline" label="Coach is typing…" />
                    )}
                  </div>
                );
              }
              return null;
            })
          )}
        </div>

        <div className={composerStyle}>
          <textarea
            className={textareaStyle}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask the coach a question…"
            rows={1}
            autoFocus
            data-testid="ask-input"
          />
          <button
            type="button"
            className={sendBtnStyle}
            onClick={send}
            disabled={streaming || !draft.trim()}
            data-testid="ask-send"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};
